import React, { useEffect, useRef } from "react";

import { Button, DocumentListItem, Typography } from "@king-ict/eupisi-ui/components";
import { ReactComponent as UploadIcon } from "@king-ict/eupisi-ui/components/Icon/Upload.svg";
import { DocumentData } from "@king-ict/eupisi-ui/models";
import { saveAs } from "file-saver";
import { FormControl, FormGroup, FormLabel, FormText } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { downloadDocument } from "../../api/nisooApi";
import { DocumentType } from "../../models/Document";
import styles from "./UploadButton.module.scss";


export interface UploadButtonProps {
  className?: string;
  name?: string;
  id?: string;
  label?: string;
  onFileAdded?: (file: File, event: React.ChangeEvent<HTMLInputElement>) => void;
  onFileRemoved?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  helperText?: string;
  errorText?: string;
  documentType: number;
  value?: DocumentData;
  toolTip?: string;
  fileFilter?: string;
}

export default function UploadButton({
  className,
  name,
  id,
  label,
  onFileAdded,
  onFileRemoved,
  disabled,
  helperText,
  errorText,
  documentType,
  value,
  toolTip,
  fileFilter
}: UploadButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [document, setDocument] = React.useState<DocumentData | null>();

  const { t } = useTranslation();  

  useEffect(() => {
    setDocument(value);
    if (fileFilter && inputRef.current) {
      inputRef.current.accept = fileFilter;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleUpload = () => {
    inputRef.current?.click();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (inputRef.current?.files && inputRef.current.files.length > 0) {
      onFileAdded && onFileAdded(inputRef.current?.files[0], e)
    }
    else {
      onFileRemoved && onFileRemoved(e)
    }
  };


  const handleRemove = (doc: DocumentData) => {
    const nativeInputFiles = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      "value"
    );
    if (nativeInputFiles) {
      const nativeInputFilesSetter = nativeInputFiles.set;
      if (nativeInputFilesSetter) {
        nativeInputFilesSetter.call(inputRef.current!, null);

        var ev = new Event("change");
        inputRef.current!.dispatchEvent(ev);
        handleChange(ev as unknown as React.ChangeEvent<HTMLInputElement>);
      }
    }
  };

  const handleDownload = (doc: DocumentData) => {
        
    if (documentType === undefined || doc.id === undefined) {
      return;
    }
    else if (doc.id === -1){
      saveAs(doc.file!, doc.fileName);
      return;
    }
    downloadDocument(documentType, doc.id);
  }

  return (
    <>
      <FormGroup className={className}>
        <FormLabel className={`${styles.buttonLabel} ${errorText ? styles.errorText : ""}`}>{label}</FormLabel>
        <FormControl
          hidden
          ref={inputRef}
          type="file"
          id={id}
          name={name}
          onChange={handleChange}
        />
        {document ? (
          <DocumentListItem document={document}
            onRemoveClick={handleRemove}
            onDocumentClick={handleDownload}
            customAction={disabled ? <span></span> : undefined}
            className={styles.fileNameCaption}
          />
        ) : (
          <>
            <Button
              outline
              color={errorText ? "danger" : "primary"}
              className={styles.button}
              icon={<UploadIcon />}
              onClick={handleUpload}
              disabled={disabled}
            >
              {t("common.addDocument")}
              {toolTip &&
                <span className={styles.tooltiptext}>{toolTip}</span>
              }
            </Button>
          </>
        )}
        {errorText || helperText ? (
          <FormText
            className={`${styles.helperText} ${errorText ? styles.errorText : ""
              }`}
          >
            <Typography color={errorText ? "danger" : "base"} variant="small">
              {errorText || helperText}
            </Typography>
          </FormText>
        ) : null}
      </FormGroup>
    </>
  );
}
